import React from 'react';
import { 
  LayoutDashboard,
  Megaphone,
  BookOpen,
  Calculator,
  TrendingUp,
  Settings,
  Users,
  Building2,
  UserCheck,
  Shield,
  HandHeart,
  Zap,
  Search,
  User
} from 'lucide-react';

import { useAuth } from '../Services/AuthContext';

interface SidebarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
}

export function Sidebar({ activeSection, setActiveSection }: SidebarProps) {

  let {canAccessModule} = useAuth();
  
  // Mock announcements data to calculate unread count
  // In a real app, this would come from a context or API
  const announcements = [
    { id: 1, isRead: false },
    { id: 2, isRead: true },
    { id: 3, isRead: true },
    { id: 4, isRead: false },
    { id: 5, isRead: true }
  ];
  
  const unreadAnnouncementsCount = announcements.filter(a => !a.isRead).length;

  // Core features available to most users
  const coreMenuItems: Array<{
    id: string;
    label: string;
    icon: any;
    hasNotification?: boolean;
  }> = [
    {
      id: 'My Workspace',
      label: 'My Workspace',
      icon: LayoutDashboard
    },
    {
      id: 'Announcements',
      label: 'Announcements',
      icon: Megaphone,
      hasNotification: unreadAnnouncementsCount > 0
    },
    {
      id: 'Sales & Renewals',
      label: 'Sales & Renewals',
      icon: TrendingUp
    },
    {
      id: 'Finance',
      label: 'Finance',
      icon: Calculator
    },
    {
      id: 'Price Lookup',
      label: 'Price Lookup',
      icon: Search
    },
    {
      id: 'Information Hub',
      label: 'Information Hub',
      icon: BookOpen
    },
    {
      id: 'Partner Administration',
      label: 'Partner Administration',
      icon: HandHeart
    }
  ];

  // System administration modules for admins
  const adminMenuItems: Array<{
    id: string;
    label: string;
    icon: any;
  }> = [
    {
      id: 'Content Management',
      label: 'Content Management',
      icon: Settings
    },
    {
      id: 'Partner Management',
      label: 'Partner Management',
      icon: Building2
    },
    {
      id: 'User Management',
      label: 'User Management',
      icon: UserCheck
    },
    {
      id: 'Role Management',
      label: 'Role Management',
      icon: Shield
    },
    {
      id: 'integration-management',
      label: 'System Management',
      icon: Zap
    }
  ];

  // Filter menu items based on user permissions
  const accessibleCoreItems = coreMenuItems.filter(item => canAccessModule(item.label));
  const accessibleAdminItems = adminMenuItems.filter(item => canAccessModule(item.label));

  const renderMenuItem = (item: any) => {
    const IconComponent = item.icon;
    const isActive = activeSection === item.id;
    
    return (
      <button
        key={item.id}
        onClick={() => setActiveSection(item.id)}
        className={`
          w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg
          transition-all duration-200 group relative overflow-hidden
          ${isActive 
            ? 'glass-nav-item active text-worldsview-blue bg-worldsview-blue/5 border-worldsview-blue/20' 
            : 'glass-nav-item hover:bg-muted/20'
          }
        `}
        aria-label={`Navigate to ${item.label}`}
      >
        <div className={`
          flex-shrink-0 transition-colors duration-200
          ${isActive 
            ? 'text-worldsview-blue' 
            : 'text-muted-foreground group-hover:text-foreground'
          }
        `}>
          <IconComponent className="w-4 h-4" />
        </div>
        
        <span className={`
          font-medium text-sm transition-colors duration-200 flex-1
          ${isActive ? 'text-worldsview-blue' : 'text-foreground'}
        `}>
          {item.label}
        </span>
        
        {/* Notification indicator for announcements */}
        {item.id === 'announcements' && item.hasNotification && (
          <div className="w-2 h-2 bg-worldsview-blue rounded-full ml-auto flex-shrink-0" />
        )}

        {isActive && (
          <div className="absolute right-0 top-0 bottom-0 w-1 bg-worldsview-blue rounded-l-full" />
        )}
      </button>
    );
  };

  return (
    <aside className="w-64 glass-sidebar h-[calc(100vh-4rem)] fixed top-16 left-0 overflow-y-auto z-40">
      <div className="p-4 space-y-1">
        {/* Core Features Section */}
        <div className="space-y-2">
          {accessibleCoreItems.map(renderMenuItem)}
        </div>

        {/* Separator and Admin Section */}
        {accessibleAdminItems.length > 0 && (
          <>
            <div className="py-3">
              <div className="px-3 mb-3">
                <div className="h-px bg-border opacity-50"></div>
              </div>
            </div>
            <div className="space-y-2">
              {accessibleAdminItems.map(renderMenuItem)}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}